/*
  Enrollment progress bar.

  The wizard already tracks its own position in the right-hand cart sidebar,
  but that column sits below the form on narrow screens and reads as an order
  summary rather than a step indicator -- people reached step 2 without knowing
  there were four, and did not realise the last step was the end of enrollment.

  This renders the same sequence across the top of the enrollment window. It
  owns no state: the steps, their labels and the current position are all read
  back out of the existing markup, so the two indicators cannot disagree. In
  particular membership.js hides the sponsor step and renumbers the sidebar for
  the non-sponsor flow (isSponsorFlow), and reading the DOM picks that up for
  free.
*/
(function () {
  var root = document.querySelector("[data-enrollment-progress]");
  if (!root) return;

  var countEl = root.querySelector("[data-progress-count]");
  var currentEl = root.querySelector("[data-progress-current]");
  var stepsEl = root.querySelector("[data-progress-steps]");
  var noteEl = root.querySelector("[data-progress-note]");

  // data-index of the review/checkout panel (.membership.finish-step).
  var FINISH_INDEX = "4";

  function isVisible(el) {
    return !!el && window.getComputedStyle(el).display !== "none";
  }

  // The sidebar entry carries a number badge and, on the optional steps, a
  // second "(optional)" line. Neither belongs in a compact top bar. Labels come
  // from section settings and never change, but the entries they live in gain
  // and lose chosen products constantly, so read each one once.
  function labelFor(el) {
    if (el.dataset.progressLabel) return el.dataset.progressLabel;

    var label;
    var primary = el.querySelector(".product-options");
    if (primary) {
      label = primary.textContent.trim();
    } else {
      var clone = el.cloneNode(true);
      clone.querySelectorAll(".product-number-img, .step-product-list").forEach(function (node) {
        node.remove();
      });
      label = clone.textContent.replace(/\s+/g, " ").trim();
    }

    el.dataset.progressLabel = label;
    return label;
  }

  function readSteps() {
    var steps = [];

    // Every .membership panel is display:none unless it is the current step, so
    // the sponsor panel's own visibility says nothing about whether this flow
    // includes it. membership.js's non-sponsor branch hides the sidebar entry
    // with an inline style -- that is the signal to read.
    var sponsorSidebar = document.querySelector(".sponsor-sidebar-step");
    var hasSponsorStep =
      !!document.querySelector(".membership.sponsor-step") && isVisible(sponsorSidebar);
    if (hasSponsorStep) {
      steps.push({ index: "0", source: sponsorSidebar });
    }

    document
      .querySelectorAll(".membership-cart .all-step li[data-index]")
      .forEach(function (li) {
        var index = li.getAttribute("data-index");
        // The sponsor flow should read as four steps, not five: "Finish up" is
        // only the review-and-checkout screen for the subscription step, so it
        // is left off the bar and counted as part of the step before it.
        if (hasSponsorStep && index === FINISH_INDEX && steps.length) {
          steps[steps.length - 1].alsoIndex = index;
          return;
        }
        steps.push({ index: index, source: li });
      });

    return steps;
  }

  function activeIndex() {
    var active = document.querySelector(".membership.active[data-index]");
    return active ? active.getAttribute("data-index") : null;
  }

  var lastSignature = "";

  function render() {
    var steps = readSteps();
    if (!steps.length) {
      root.hidden = true;
      return;
    }

    var current = activeIndex();
    var position = steps.findIndex(function (step) {
      return step.index === current || step.alsoIndex === current;
    });
    // Before the wizard marks a panel active there is nothing to point at; show
    // the first step rather than an empty bar.
    if (position < 0) position = 0;

    // Cheap guard first: the wizard rewrites this subtree constantly (the
    // checkout gate alone re-checks every 300ms) and each of those mutations
    // calls back in here. Only touch the DOM when the bar would actually differ.
    var signature = steps
      .map(function (step) {
        return step.index;
      })
      .join("|") + "@" + position + "@" + current;
    if (signature === lastSignature) return;
    lastSignature = signature;

    stepsEl.innerHTML = "";
    steps.forEach(function (step, i) {
      var li = document.createElement("li");
      li.className = "enrollment-progress__step";
      if (i < position) li.classList.add("is-complete");
      if (i === position) {
        li.classList.add("is-current");
        li.setAttribute("aria-current", "step");
      }

      var marker = document.createElement("span");
      marker.className = "enrollment-progress__marker";
      marker.textContent = i < position ? "✓" : String(i + 1);

      var label = document.createElement("span");
      label.className = "enrollment-progress__label";
      label.textContent = labelFor(step.source);

      li.appendChild(marker);
      li.appendChild(label);
      stepsEl.appendChild(li);
    });

    // Drives the compact rail used instead of the labelled steps on phones,
    // where four or five labels side by side are unreadable.
    root.style.setProperty(
      "--enrollment-progress-fill",
      ((position + 1) / steps.length) * 100 + "%"
    );

    countEl.textContent = "Step " + (position + 1) + " of " + steps.length;
    currentEl.textContent = labelFor(steps[position].source);

    // Reaching the last step is the part people missed: it is checkout, and
    // enrollment is done once payment clears.
    // With "Finish up" folded away the last step also covers the subscription
    // screen, so key the note to the checkout panel itself.
    if (noteEl) {
      noteEl.hidden =
        position !== steps.length - 1 ||
        (!!steps[position].alsoIndex && current !== steps[position].alsoIndex);
    }

    root.hidden = false;
  }

  render();
  document.addEventListener("DOMContentLoaded", render);

  // The wizard moves between steps by toggling classes (and hides the sponsor
  // step with an inline style), so watch for both rather than hooking every
  // button that can advance the flow.
  var container = document.getElementById("enrollment-containers") || document.body;
  new MutationObserver(function () {
    window.requestAnimationFrame(render);
  }).observe(container, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["class", "style", "data-index"],
  });
})();
